import { css } from "@emotion/react";
import React, { forwardRef } from "react";
import { AsComponentProp } from "../AsComponentProps";
import { PartialColorProps, useTheme } from "../theme";
import { resetButtonStyle } from "../Button";

export interface BaseTextButtonProps extends PartialColorProps {
  children?: React.ReactNode;
  icon?: React.ReactNode;
}

const defaultElement = "button";

export type TextButtonProps<E extends React.ElementType = "button"> =
  AsComponentProp<E, BaseTextButtonProps>;

export const TextButton = forwardRef<
  HTMLElement,
  TextButtonProps<React.ElementType>
>((props, ref) => {
  const {
    children,
    color = "default",
    as,
    icon,
    ...htmlProps
  } = props as unknown as TextButtonProps<React.ElementType> & {
    color?: string;
  }; // workaround for TS complaints on color indexing
  const theme = useTheme();

  const Component = as ?? defaultElement;

  const hasIcon = icon !== undefined && icon !== null;
  const hasChildren = children !== undefined && children !== null;
  return (
    <Component
      ref={ref}
      {...htmlProps}
      css={[
        resetButtonStyle,
        css`
          font-size: 1em;
          color: ${color === "default"
            ? theme.palette.text.primary
            : theme.palette.intentions[color].main};
          transition: color ${theme.transitions.duration.short}
            ${theme.transitions.easing.easeInOut};
          &:hover {
            color: ${color === "default"
              ? theme.palette.text.primary
              : theme.palette.intentions[color].dark};
          }
        `,
        hasIcon
          ? css`
              vertical-align: text-bottom;
            `
          : css`
              padding: 0px 4px;
              vertical-align: baseline;
              border-bottom: 1px solid currentColor;
            `,
      ]}
    >
      {icon}
      {hasChildren ? (
        <span
          css={css`
            align-self: flex-end;
          `}
        >
          {children}
        </span>
      ) : null}
    </Component>
  );
}) as <E extends React.ElementType = "button">(
  props: TextButtonProps<E> & { ref?: React.ForwardedRef<HTMLElement> },
) => React.ReactElement;
